/**
 * /payouts/hold — payout-hold operator page for Feature #138.
 *
 * Declares a TanStack Router route and renders `PayoutHoldPage`. The
 * component is imported by the root-owned `router.tsx` via module
 * augmentation — agents never touch `router.tsx` themselves; the root
 * wire-up cycle picks this route up after the prod merge.
 *
 * Search params: `?affiliate=aff_xxx` lets operators deep-link to a
 * specific affiliate's hold config.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PayoutHoldPage } from "@/features/payout-hold/PayoutHoldPage";
import { isValidAffiliateId } from "@/features/payout-hold/api";

// ---------------------------------------------------------------------------
// Route — mounts at `/payouts/hold`.
// ---------------------------------------------------------------------------

interface PayoutHoldSearch {
	affiliate?: string;
}

export const payoutsHoldRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/payouts/hold",
	validateSearch: (raw: Record<string, unknown>): PayoutHoldSearch => {
		const affiliate = raw.affiliate;
		if (isValidAffiliateId(affiliate)) return { affiliate };
		return {};
	},
	component: PayoutsHoldRoute,
});

export default payoutsHoldRoute;

// ---------------------------------------------------------------------------
// Page shell — static breadcrumb + the live feature component.
// ---------------------------------------------------------------------------

export function PayoutsHoldRoute() {
	const { affiliate } = payoutsHoldRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<PayoutHoldPage initialAffiliateId={affiliate} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Payouts</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Hold</li>
			</ol>
		</nav>
	);
}
